/**
 * What goes in the generated event pack, decided here rather than in the template.
 *
 * The pack is a printed document: a row whose value is missing is a blank line on a page someone
 * is holding, not a gap that is obviously nothing. So these helpers drop what the organiser never
 * filled in, and the template prints exactly what it is handed.
 *
 * Kept free of Angular so the content decisions are testable without a TestBed - which matters
 * because they are the part of the pack that can actually be wrong.
 */
import { AppEvent, EventInfoSection } from './models';

export interface PackDetailRow {
    /** Translation key. The pack is rendered through ngx-translate like the rest of the app. */
    labelKey: string;
    value: string;
    /**
     * How the template should render `value`:
     * - 'text'  print as-is
     * - 'date'  an ISO string, to go through the date pipe
     * - 'i18n'  a translation key, not a display string
     *
     * The alternative was formatting here, which would mean a helper that knows the user's locale
     * and date format. The template already has pipes for both, so it keeps what it is better at.
     */
    kind: 'text' | 'date' | 'i18n';
}

/** Mirrors PairingModeKeyPipe, so the pack says "Swiss" where the detail page says "Swiss". */
const PAIRING_MODE_KEYS: Record<string, string> = {
    swiss: 'GLOBAL.EVENTS.PAIRING_MODE.SWISS',
    round_robin: 'GLOBAL.EVENTS.PAIRING_MODE.ROUND_ROBIN',
    random: 'GLOBAL.EVENTS.PAIRING_MODE.RANDOM'
};

const present = (value: string | undefined | null): boolean => !!value && !!value.trim();

/**
 * The venue address, top down, with the parts the organiser left out removed.
 *
 * Only `countryCode` is required on an event, so most addresses have gaps.
 */
export const packAddressLines = (event: AppEvent): string[] => {
    const location = event?.location;
    if (!location) {
        return [];
    }

    return [location.venueName, location.city, location.state, location.postalCode, location.countryCode]
        .filter(present)
        .map((line) => line!.trim());
};

/**
 * The event's particulars, as label/value rows.
 *
 * A row is included only when it has something to say. `pointsLimit` is tested for null rather
 * than falsiness: a zero limit is a real answer, and treating it as missing would silently drop it.
 */
export const packDetailRows = (event: AppEvent): PackDetailRow[] => {
    const rows: PackDetailRow[] = [];
    const add = (labelKey: string, value: string | number | undefined | null, kind: PackDetailRow['kind'] = 'text') => {
        if (value === undefined || value === null || `${value}`.trim() === '') {
            return;
        }
        rows.push({ labelKey, value: `${value}`.trim(), kind });
    };

    add('GLOBAL.EVENTS.DETAIL.ROUNDS', event?.numberOfRounds);
    add('GLOBAL.EVENTS.PACK.POINTS_LIMIT', event?.pointsLimit);
    add('GLOBAL.EVENTS.DETAIL.PAIRING_MODE', event?.pairingMode && PAIRING_MODE_KEYS[event.pairingMode], 'i18n');
    add('GLOBAL.EVENTS.DETAIL.CAPACITY', event?.capacity);
    // No list COUNT: the document names each list in a table below these rows, and a count
    // directly above that table says nothing the table does not.
    add('GLOBAL.EVENTS.PACK.TICKET_PRICE', present(event?.currency) ? `${event.ticketPrice} ${event.currency}` : event?.ticketPrice);
    add('GLOBAL.EVENTS.DETAIL.LIST_SUBMISSION_DEADLINE', event?.listSubmissionDate, 'date');

    return rows;
};

/**
 * The organiser's free-form sections - parking, food, house rules, whatever they chose to write.
 *
 * Sections with neither a heading nor a body are dropped: the array editor creates an empty row
 * the moment "add" is pressed, so one abandoned half-way through would otherwise print as a gap.
 * A section with a heading and no body is kept - half-written is still intent, and silently losing
 * it would read as the app having eaten it.
 */
export const packInfoSections = (event: AppEvent): EventInfoSection[] =>
    (event?.infoSections ?? []).filter((section) => present(section?.heading) || present(section?.body));
